import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styles from "./CreditCards.module.css";
import Headers from "../components/Header";
import CardList from "../components/CardList";
import CardModal from "../components/CardModal";
import WarningPrimaryModal from "components/WarningPrimaryModal";
import Loader from "components/Loader";
import Button from "components/Button";
import { useAccount } from "utils/AccountInfoContext";
import { useCreditCards } from "./useCreditCards";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useTranslation } from "react-i18next";

export function CreditCards() {
  const { t } = useTranslation();
  const history = useHistory();
  const { billingData, refetchIssuesData } = useAccount();
  const {
    data,
    isError,
    isLoading,
    refetch,
    deleteCard,
    updateCard,
    addCustomer,
    addCard,
  } = useCreditCards();

  const [openModal, setOpenModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showBillingWarningModal, setShowBillingWarningModal] = useState(false);
  const [pendingPrimaryIndex, setPendingPrimaryIndex] = useState(null);
  const [previousCards, setPreviousCards] = useState([]);
  const [cards, setCards] = useState([]);
  const [primaryCard, setPrimaryCard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCard, setCurrentCard] = useState(null);
  const [postalCode, setPostalCode] = useState("");
  const [nickName, setNickName] = useState("");
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [triggerCollectClick, setTriggerCollectClick] = useState(false);

  // Load cards from API
  useEffect(() => {
    if (data) {
      let cardArray = [];
      data.cards.forEach((card) => {
        const cardInfo = {
          lastFour: card.lastFour,
          expMonth: card.expireMonth,
          expYear: card.expireYear,
          nickName: card.nickname,
          zipCode: card.zip,
          isPrimary: card.isPrimary,
          id: card.membershipCreditCardId,
          cardHolder: card.cardHolder,
          membershipCreditCardId: card.membershipCreditCardId,
          cardType: card.cardType || "credit-card",
        };

        if (card.isPrimary) {
          setPrimaryCard(cardInfo);
        }

        cardArray.push(cardInfo);
      });
      setCards(cardArray);
    }
  }, [data]);

  // Refetch if needed
  useEffect(() => {
    const shouldRefetch = localStorage.getItem("shouldRefetch") === "true";

    if (shouldRefetch) {
      refetchIssuesData();
      localStorage.removeItem("shouldRefetch");
    }
  }, []);

  const handleTokenReceived = async (response) => {
    console.log("Token received in CreditCards component:", response);
    const { token, card } = response;
    if (!token) return;

    setIsModalLoading(true);
    console.log("Received token:", token);

    try {
      const customerData = await addCustomer.mutateAsync({
        paymentToken: token,
        postalCode,
      });

      let expMonth, expYear, expiration;
      if (customerData.cc_exp.length === 3) {
        expMonth = `0${customerData.cc_exp.slice(0, 1)}`;
        expYear = customerData.cc_exp.slice(1);
        expiration = `${expMonth}${expYear}`;
      } else if (customerData.cc_exp.length === 4) {
        expMonth = customerData.cc_exp.slice(0, 2);
        expYear = customerData.cc_exp.slice(2);
        expiration = `${expMonth}${expYear}`;
      } else {
        expiration = customerData.cc_exp;
      }

      const newCardData = {
        cardHolder: `${customerData.first_name} ${customerData.last_name}`,
        expiration: expiration,
        firstSix: customerData.cc_bin,
        isPrimary: false,
        lastFour: customerData.lastFour,
        nickname: nickName,
        postalCode: postalCode,
        token: customerData.customer_vault_id,
        cardType: customerData.cardType || "credit-card",
      };

      await addCard.mutateAsync(newCardData);

      // Close modal after success
      setOpenModal(false);
      // trigger refetch instead of reload
      localStorage.setItem("shouldRefetch", "true");
      refetchIssuesData();
    } catch (error) {
      console.error(t("Error during the card addition"), error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleAdd = async (cardInfo) => {
    setCurrentCard(cardInfo);
    setPostalCode(cardInfo?.zipCode);
    setNickName(cardInfo?.nickName);

    setTriggerCollectClick((prev) => !prev);
  };

  const handleTogglePrimary = async (cardId) => {
    const primaryCount = cards.filter((card) => card.isPrimary).length;
    const cardToToggle = cards.find((card) => card.id === cardId);

    if (cardToToggle.isPrimary && primaryCount === 1) {
      setShowWarningModal(true);
      setPendingPrimaryIndex(cardId);
    } else {
      if (billingData === true) {
        setPreviousCards(cards);
        setShowBillingWarningModal(true);
      } else {
        const updatedCards = cards.map((card) => ({
          ...card,
          isPrimary: card.id === cardId,
        }));
        setCards(updatedCards);

        const updatedCardData = {
          cardHolder: cardToToggle.cardHolder,
          expireMonth: cardToToggle.expMonth,
          expireYear: cardToToggle.expYear.slice(2),
          nickname: cardToToggle.nickName,
          zip: cardToToggle.zipCode,
          membershipCreditCardId: cardToToggle.membershipCreditCardId,
          isPrimary: true,
          cardType: cardToToggle.cardType || "credit-card",
        };
        await updateCard.mutateAsync(updatedCardData);
        setPrimaryCard(cardId);
      }
    }
  };

  const handleConfirmPrimaryChange = () => {
    setShowWarningModal(false);
    setPendingPrimaryIndex(null);
  };

  const handleBillingWarningClose = () => {
    setShowBillingWarningModal(false);
    setCards(previousCards);
  };

  const handleDelete = async (membershipCreditCardId) => {
    await deleteCard.mutateAsync(membershipCreditCardId);
    // Trigger refetch instead of reload
    refetchIssuesData();
  };

  const handleUpdateCard = async (card) => {
    let findCard = cards.find(
      (c) => c.membershipCreditCardId === card.membershipCreditCardId
    );

    const updatedCardData = {
      cardHolder: card.cardHolder,
      expireMonth: card.expMonth,
      expireYear: card.expYear.slice(2),
      nickname: card.nickName,
      zip: card.zipCode,
      membershipCreditCardId: card.membershipCreditCardId,
      isPrimary: card.isPrimary,
      cardType: findCard.cardType || card.cardType || "credit-card",
    };
    await updateCard.mutateAsync(updatedCardData);
    setOpenModal(false);
    setIsEditing(false);
    refetchIssuesData();
  };

  const handleHeaderBackClick = () => {
    history.push("/");
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Headers header={t("Settings")} onClick={handleHeaderBackClick} />
      </div>

      <Button
        buttonText={t("Add Card")}
        className={styles.addNew}
        onClick={() => {
          setCurrentCard(null);
          setIsEditing(false);
          setOpenModal(true);
        }}
        disabled={billingData === true || isLoading}
      />

      <div>
        <CardList
          cards={cards}
          setPrimary={handleTogglePrimary}
          canEdit={billingData}
          onDelete={handleDelete}
          onUpdate={handleUpdateCard}
        />
      </div>

      <CardModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        card={currentCard}
        onAdd={handleAdd}
        onValidate={handleUpdateCard}
        isEditing={isEditing}
        onTokenReceived={handleTokenReceived}
        isLoading={isModalLoading}
      />

      {showWarningModal && (
        <WarningPrimaryModal
          isOpen={showWarningModal}
          title={t("You are required a primary card")}
          description={t("Please add a new card or select card")}
          onClick={handleConfirmPrimaryChange}
        />
      )}

      {showBillingWarningModal && (
        <WarningPrimaryModal
          isOpen={showBillingWarningModal}
          title={t("Primary Card Change Restricted")}
          description={t("The ability to change card is disabled")}
          onClick={handleBillingWarningClose}
        />
      )}
    </div>
  );
}

export default CreditCards;
